import { FC, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Order } from '../../api/fetchAllHEOrder';
import {
    ActiveTab,
    FilterData,
    OrderStatusFilterType,
    orderFiltersObj,
} from '../../pages/HEDashboard';
import { FeelGoodComponent } from '../../pages/HEDashboardUtils';
import { isDoctor } from '../../pages/utils';
import { showDialog } from '../../store/actions/prescriptionPad';
import { GlobalState } from '../../store/reducers';
import { getGenieSetting } from '../../store/selectors/session';

type HEDashboardStatusFilterBoxType = {
    isLoading: boolean;
    label: string;
    onClick: () => void;
    isSelected: boolean;
    count?: number;
    amount?: number;
    id: string;
};
const HEDashboardStatusFilterBox: FC<HEDashboardStatusFilterBoxType> = ({
    isLoading,
    label,
    onClick,
    isSelected,
    count,
    amount,
    id,
}) => {
    return (
        <div id={id}>
            <div
                className={`select-none px-16 py-8 space-y-4 border cursor-pointer rounded-4 text-13 min-w-160 ${
                    isSelected
                        ? 'border-actionPrimary text-actionPrimary'
                        : 'border-neutral-7 text-neutral-1'
                } ${isLoading ? 'pointer-events-none' : ''}`}
                onClick={onClick}
            >
                <p className={`${isSelected ? 'font-700' : 'font-500'}`}>{label}</p>
                <div className="flex items-baseline justify-between space-x-8">
                    {isLoading ? (
                        <div className="w-full h-32 shimmer"></div>
                    ) : !count && !amount ? (
                        <div className="w-full leading-7 text-18 min-h-32">-</div>
                    ) : (
                        <Fragment>
                            {count && <p className="text-21">{count}</p>}
                            {amount && <p>(₹ {amount.toLocaleString('en-IN')})</p>}
                        </Fragment>
                    )}
                </div>
            </div>

            {isSelected && (
                <div className="w-16 h-16 flex items-center justify-center mx-auto">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320 512"
                        className="w-16 h-16 fill-actionPrimary"
                    >
                        <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
                    </svg>
                </div>
            )}
        </div>
    );
};

const HEDashboardStatusFilters: FC<{
    data?: FilterData;
    isLoading: boolean;
    orderStatusFilter: OrderStatusFilterType;
    setFilter: (pf: OrderStatusFilterType) => void;
    activeTab: ActiveTab;
    showFeelGoodData: boolean;
    transactions: Order[];
    toggleFeelGoodData: () => void;
}> = ({
    data,
    isLoading,
    orderStatusFilter,
    setFilter,
    activeTab,
    transactions,
    showFeelGoodData,
    toggleFeelGoodData,
}): JSX.Element => {
    const isHealthWeekDoctorFlag = useSelector((state: GlobalState) =>
        getGenieSetting(state, 'isHealthWeekDoctor'),
    );
    const isHealthWeekDoctor = isHealthWeekDoctorFlag && isDoctor();
    const dispatch = useDispatch();
    const ids = ['not-ordered', 'pending-orders', 'delivered-orders'];
    const showHealthWeek = () => {
        dispatch(showDialog('health-week'));
    };
    return (
        <div className="flex mt-16 space-x-16 px-16">
            {Object.entries(orderFiltersObj).map(([key, value], index) => (
                <HEDashboardStatusFilterBox
                    key={key}
                    label={value.label[activeTab]}
                    onClick={() => setFilter(key as OrderStatusFilterType)}
                    isSelected={orderStatusFilter === key}
                    isLoading={isLoading}
                    count={data && data[key as OrderStatusFilterType].count}
                    amount={data && data[key as OrderStatusFilterType].amount}
                    id={ids[index]}
                />
            ))}
            {isHealthWeekDoctor && (
                <div
                    className="w-232 h-74 px-12 py-9 flex items-start space-x-16 rounded-4 cursor-pointer"
                    style={{ background: 'linear-gradient(93deg, #19A66A 0%, #0F6440 97.81%)' }}
                    onClick={showHealthWeek}
                >
                    <div className="pt-3">
                        <svg
                            className="w-20 h-22 fill-white"
                            viewBox="0 0 18 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M17.5507 12.564C17.3843 12.2547 17.1762 11.9722 16.9336 11.7238L15.6221 10.395L14.361 11.6727L15.6569 12.9858C16.0024 13.3355 16.216 13.819 16.216 14.3532C16.216 14.8874 16.0177 15.3356 15.6939 15.6824L15.6928 15.6835C15.6808 15.696 15.6689 15.7085 15.6569 15.721C14.8204 16.5895 13.7946 17.2707 12.6474 17.6961C11.7811 18.0172 10.8452 18.1924 9.86948 18.1924C9.04241 18.1924 8.24469 18.0668 7.49341 17.8328C6.87771 17.6417 6.29394 17.3776 5.75076 17.0513C3.37613 15.624 1.78398 13.0005 1.78398 10C1.78398 6.99953 3.37613 4.37565 5.75076 2.94833C6.29576 2.62099 6.88206 2.35647 7.49995 2.16504C8.24942 1.93211 9.04531 1.80719 9.86985 1.80719C10.8431 1.80719 11.776 1.98133 12.6401 2.30062C13.7899 2.72569 14.8178 3.40718 15.6558 4.27754C15.667 4.28891 15.6779 4.30033 15.6888 4.3117C16.0155 4.65889 16.216 5.12877 16.216 5.64645C16.216 6.16408 16.0024 6.66371 15.6569 7.01384L15.3636 7.31143L13.9712 8.72183L10.9355 5.64607C10.8358 5.54611 10.7307 5.45208 10.6204 5.36392C9.99277 4.86424 9.20085 4.56594 8.34045 4.56594C8.03514 4.56594 7.73854 4.60341 7.4546 4.67466C7.25263 4.72464 7.0572 4.79186 6.87009 4.87452C6.85559 4.88078 6.8411 4.88741 6.82698 4.89399C5.54228 5.47927 4.64774 6.78754 4.64774 8.30742C4.64774 8.40037 4.65102 8.4926 4.65789 8.58408C4.65789 8.58777 4.65826 8.59142 4.65864 8.59549C4.66008 8.6179 4.66228 8.64031 4.66443 8.66271C4.71086 9.06315 4.95272 9.40337 5.29065 9.5823C5.45382 9.66861 5.63944 9.7175 5.83636 9.7175H5.83814C6.18589 9.71712 6.49882 9.56392 6.71421 9.32071C6.90202 9.10943 7.01624 8.82951 7.01624 8.52235C7.01624 8.41543 7.00245 8.31221 6.97673 8.21377C6.91034 7.96208 6.76494 7.74275 6.56913 7.58552C6.85162 6.87499 7.53801 6.37351 8.34045 6.37351C8.86764 6.37351 9.34481 6.58991 9.69037 6.94003L12.7101 9.99964L11.9258 10.7943L9.67405 13.0758C9.32994 13.4164 8.85932 13.6262 8.34045 13.6262C8.06488 13.6262 7.80268 13.567 7.56592 13.4604C7.56447 13.4597 7.56265 13.459 7.56087 13.4583C7.11124 13.254 6.75372 12.8789 6.56913 12.4145C6.76494 12.2569 6.91034 12.0379 6.97673 11.7859C7.00245 11.6878 7.01624 11.5842 7.01624 11.4776C7.01624 11.1705 6.90202 10.8906 6.71421 10.679C6.49882 10.4361 6.18659 10.2829 5.83889 10.2822H5.83636C5.63944 10.2822 5.45382 10.331 5.29065 10.4174C4.95159 10.597 4.70941 10.9387 4.66406 11.3406C4.66373 11.3458 4.66298 11.3505 4.66261 11.3553C4.66116 11.3715 4.65971 11.3873 4.65864 11.4034C4.65864 11.4078 4.65864 11.4119 4.65789 11.4159C4.65102 11.5071 4.64774 11.5993 4.64774 11.6918C4.64774 13.2117 5.54228 14.5204 6.82698 15.1056L6.87009 15.1251C7.0572 15.2078 7.25263 15.275 7.4546 15.325C7.73854 15.3963 8.03514 15.4337 8.34045 15.4337C9.20305 15.4337 9.99642 15.1343 10.6248 14.6321C10.6846 14.5843 10.743 14.5343 10.7999 14.4825L10.8014 14.4811L13.5444 11.7018L13.9672 11.2734L15.2283 9.99562L15.6018 9.61754L15.9292 9.2858L16.914 8.28762C17.1639 8.03447 17.3781 7.74498 17.5485 7.42792C17.8336 6.89702 17.9957 6.28862 17.9957 5.64204C17.9957 5.33488 17.959 5.03658 17.8901 4.75112C17.7346 4.10558 17.414 3.52584 16.976 3.06075C16.9626 3.04639 16.9492 3.03247 16.935 3.01849C16.9332 3.01664 16.9318 3.01518 16.9303 3.01333C16.9191 3.00234 16.9082 2.99092 16.8973 2.97993C16.8948 2.97699 16.8923 2.97477 16.8897 2.97254C15.1007 1.13745 12.6165 0 9.86985 0C8.53695 0 7.26604 0.267462 6.10613 0.752787C5.52634 0.995284 4.97446 1.29212 4.45629 1.63675C1.77201 3.42447 3.04089e-08 6.50246 4.67654e-08 10C6.31216e-08 13.4975 1.77201 16.5756 4.45629 18.3629C4.97263 18.7064 5.52269 19.0025 6.10033 19.2443C7.2617 19.7314 8.5348 20 9.86985 20C12.631 20 15.1275 18.8508 16.9184 16.9988L16.9267 16.9904C16.9325 16.9844 16.9383 16.9782 16.9441 16.9724C16.9553 16.9609 16.9666 16.9492 16.9774 16.9375C16.9818 16.9334 16.9858 16.929 16.9898 16.9242C17.4213 16.4624 17.7379 15.8889 17.8927 15.2507C17.963 14.9631 18 14.6626 18 14.3532C18 13.7051 17.8368 13.0956 17.5507 12.564Z" />
                        </svg>
                    </div>

                    <div className="font-600 italic uppercase text-center text-white space-y-4">
                        <p className="text-10">Eka Health Week</p>
                        <p className="text-13 font-700 leading-5">
                            With one click earn up to ₹20,000+
                        </p>
                    </div>

                    <div className="pt-3">
                        <svg
                            className="w-10 h-14 fill-white"
                            viewBox="0 0 7 11"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M6.51562 4.98438C6.82031 5.26562 6.82031 5.75781 6.51562 6.03906L2.01562 10.5391C1.73438 10.8438 1.24219 10.8438 0.960938 10.5391C0.65625 10.2578 0.65625 9.76562 0.960938 9.48438L4.92188 5.5L0.960938 1.53906C0.65625 1.25781 0.65625 0.765625 0.960938 0.484375C1.24219 0.179688 1.73438 0.179688 2.01562 0.484375L6.51562 4.98438Z" />
                        </svg>
                    </div>
                </div>
            )}
            <FeelGoodComponent
                transactions={transactions}
                showFeelGoodData={showFeelGoodData}
                toggleFeelGoodData={toggleFeelGoodData}
            />
        </div>
    );
};

export default HEDashboardStatusFilters;
